// Shared field constants + helpers for the form editor and its panes
// (FormEditorModal, FormFieldListPane, FormFieldEditorPane). Kept in one place
// so the modal and the extracted panes can't drift.

// Field types the builder supports. Kept in sync with the backend's
// _normalize_fields VALID_FIELD_TYPES.
export const FIELD_TYPES = [
  { value: "short_text", label: "Short text" },
  { value: "long_text", label: "Long text" },
  { value: "checkbox", label: "Checkbox" },
  // Two-button Yes / No question. Unlike checkbox, "No" is a real answer.
  { value: "yes_no", label: "Yes / No" },
  { value: "single_select", label: "Single select" },
  // Pick any number of options. Never scored.
  { value: "multi_select", label: "Multi select" },
  { value: "scale", label: "Scale" },
  // The standard 0-10 "how likely are you to recommend" question. Fixed
  // bounds, never scored; the report rolls it up into an NPS.
  { value: "nps", label: "NPS (0-10)" },
  // Display-only section divider — groups fields under a label (no input,
  // never required or scored). Offered in list forms only.
  { value: "heading", label: "Section heading" },
  // Display-only screen: a headline, body text, and an optional image, with no
  // input. Slide-show forms use these as welcome, transition, and closing
  // screens. Offered in slide-show forms only.
  { value: "info_screen", label: "Info screen" },
];

// Which types the "+ Add field" menu offers. A heading is a divider in a
// scrolling form; an info screen is a screen of its own — each is meaningless
// in the other presentation, so each is offered in one only. Existing fields
// keep whatever type they were built with: a field's type is fixed once added
// (change it by deleting and adding the one you meant), so flipping the
// presentation can never retype anything.
export const typesForPresentation = (presentation) =>
  FIELD_TYPES.filter((t) =>
    presentation === "slides" ? t.value !== "heading" : t.value !== "info_screen"
  );

// Short labels for the compact rail chip.
export const FIELD_TYPE_SHORT = {
  short_text: "Short text",
  long_text: "Long text",
  checkbox: "Checkbox",
  yes_no: "Yes / No",
  single_select: "Select",
  multi_select: "Multi",
  scale: "Scale",
  nps: "NPS",
  heading: "Heading",
  info_screen: "Info screen",
};

// Types that render text but collect no answer: never required, never scored,
// never a display-condition target or a piping source, and not counted as a
// question. Mirrors DISPLAY_ONLY_FIELD_TYPES in backend/lms_info/form_logic.py.
export const DISPLAY_ONLY_TYPES = ["heading", "info_screen"];
export const isDisplayOnlyType = (type) => DISPLAY_ONLY_TYPES.includes(type);

// Types with an options list.
export const OPTION_TYPES = ["single_select", "multi_select"];
export const hasOptions = (type) => OPTION_TYPES.includes(type);

// Types whose answer is a small closed set (report filters / segments).
export const isCategoricalType = (type) =>
  type === "single_select" || type === "yes_no" || type === "multi_select" || type === "checkbox";

export const newId = () => {
  try {
    if (window.crypto?.randomUUID) return `f_${window.crypto.randomUUID().slice(0, 8)}`;
  } catch (e) {}
  return `f_${Math.random().toString(36).slice(2, 10)}`;
};

export const makeDefaultField = (type = "short_text") => {
  const base = { id: newId(), type, label: "", help: "", required: false, scored: false };
  if (hasOptions(type)) base.options = [{ label: "", points: 0 }];
  if (type === "scale") {
    base.min = 1;
    base.max = 5;
    base.min_label = "";
    base.max_label = "";
  }
  if (type === "nps") {
    base.min = NPS_MIN;
    base.max = NPS_MAX;
    base.min_label = NPS_DEFAULT_MIN_LABEL;
    base.max_label = NPS_DEFAULT_MAX_LABEL;
  }
  return base;
};

// NPS bounds are fixed (mirrors NPS_MIN / NPS_MAX on the backend). Renderers
// treat nps like a scale with these bounds.
export const NPS_MIN = 0;
export const NPS_MAX = 10;
export const NPS_DEFAULT_MIN_LABEL = "Not at all likely";
export const NPS_DEFAULT_MAX_LABEL = "Extremely likely";
export const isScaleLikeType = (type) => type === "scale" || type === "nps";
export const scaleBounds = (field) => {
  if (field?.type === "nps") return { min: NPS_MIN, max: NPS_MAX };
  return {
    min: Number.isFinite(field?.min) ? field.min : 1,
    max: Number.isFinite(field?.max) ? field.max : 5,
  };
};

// Coerce an incoming show_if rule into the editor's shape (or null).
const hydrateShowIf = (rule) => {
  if (!rule || typeof rule !== "object" || !rule.field || !rule.op) return null;
  const out = { field: String(rule.field), op: String(rule.op) };
  if (rule.op !== "answered") out.value = rule.value === undefined ? "" : rule.value;
  return out;
};

// Coerce an incoming (possibly partial) field def into the builder's shape.
export const hydrateField = (f) => {
  const type = FIELD_TYPES.some((t) => t.value === f?.type) ? f.type : "short_text";
  const field = {
    id: f?.id || newId(),
    type,
    label: f?.label || "",
    help: f?.help || "",
    required: !!f?.required,
    scored: !!f?.scored,
  };
  if (hasOptions(type)) {
    const opts = Array.isArray(f?.options) ? f.options : [];
    field.options = opts.length
      ? opts.map((o) =>
          typeof o === "string"
            ? { label: o, points: 0 }
            : { label: o?.label || "", points: Number(o?.points) || 0 }
        )
      : [{ label: "", points: 0 }];
  }
  if (type === "scale") {
    field.min = Number.isFinite(f?.min) ? f.min : 1;
    field.max = Number.isFinite(f?.max) ? f.max : 5;
    field.min_label = f?.min_label ? String(f.min_label) : "";
    field.max_label = f?.max_label ? String(f.max_label) : "";
  }
  if (type === "nps") {
    field.min = NPS_MIN;
    field.max = NPS_MAX;
    field.min_label = f?.min_label ? String(f.min_label) : NPS_DEFAULT_MIN_LABEL;
    field.max_label = f?.max_label ? String(f.max_label) : NPS_DEFAULT_MAX_LABEL;
    field.scored = false;
  }
  // Any field may carry an image: an info screen's picture, or the thing a
  // question is asking about. Stored as a LearningFile uuid; image_url is the
  // presigned preview URL the server resolves on read (never persisted — it
  // expires).
  if (f?.image_uuid) field.image_uuid = String(f.image_uuid);
  if (f?.image_url) field.image_url = String(f.image_url);
  if (type === "short_text" || type === "long_text") {
    if (f?.max_points !== undefined && f?.max_points !== null) {
      field.max_points = Number(f.max_points) || 0;
    }
    if (f?.answer_key) field.answer_key = String(f.answer_key);
  }
  const showIf = hydrateShowIf(f?.show_if);
  if (showIf) field.show_if = showIf;
  return field;
};

// scale / single_select score automatically; short_text / long_text are graded
// manually by a manager (or AI) up to a max_points cap.
export const isManualType = (type) => type === "short_text" || type === "long_text";
